import { action, computed, observable } from 'mobx';
import { get, maxBy, isEmpty, merge, reject, last } from 'lodash';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    IChildLayoutResponse,
    IContainerableBlockResponse
} from '~/types/server.types';
import type { RootStore } from '~/stores/root.store';
import {
    TLayoutType,
    IChildLayout,
    TContainerableBlockType
} from '~/types/model.types';
import { TBlock, TContainerableBlock } from '~/types/builder.types';
import { IContainerBlockStyles } from '~/types/block-styles.types';
import type { FlowRevision } from '~/models/flow-revision.model';

export interface IContainerableBlock {
    children: IChildLayout[];
    block_id: string;
}

type ContainerableBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<
        IContainerableBlockResponse,
        | 'type'
        | 'source_flow_id'
        | 'source_block_id'
        | 'layout_type'
        | 'children'
    >;

export abstract class ContainerableBlock extends BaseBlock {
    abstract type: TContainerableBlockType;
    @observable layout_type: TLayoutType;
    @observable children: IChildLayout[];
    @observable styles: IContainerBlockStyles;
    @observable source_flow_id: string;
    @observable source_block_id: string;

    constructor(
        block: IContainerableBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        const { children, ...restBlockProps } = block;

        super(restBlockProps, flowRevision, rootStore);

        this.styles = {
            ...this.styles,
            ...block.container_styles
        };

        this.layout_type = block.layout_type;
        this.children = children || [];
    }

    @computed
    get sortedChildLayouts() {
        return this.children
            .slice()
            .sort((a, b) => a.order - b.order)
            .filter(Boolean);
    }

    @computed
    get isAnyChildSelected() {
        const { selectedBlockId } = this.rootStore.builderUiStore;
        return this.childBlocks.some(
            block => block.block_id === selectedBlockId
        );
    }

    @computed
    get childBlocks() {
        // In the process of optimisticly adding a new block, it is added to it's parent
        // before it is actually applied in the store. This causes this prop to recalculate and produce
        // undefined children. That's why the filter(Boolean) is here.
        return this.sortedChildLayouts
            .map(
                childLayout => this.flowRevision.blocksMap[childLayout.block_id]
            )
            .filter(Boolean);
    }

    @computed
    get maxBlockOrder() {
        return last(this.sortedChildLayouts)?.order || 0;
    }

    @computed
    get hasErrors() {
        if (!this.rootStore.builderUiStore.showErrors) {
            return false;
        }

        return this.allDescendants().some(_block => !isEmpty(_block.errors));
    }

    @computed
    get blockInputValidations(): any {
        return this.allDescendants().reduce(
            (invalidations, child) =>
                merge(invalidations, child.blockInputValidations),
            {}
        );
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get hasOnlyEmptyContainers() {
        return this.allDescendants().every(
            child => child.type === 'container_block'
        );
    }

    // Returns all children recursively
    allDescendants() {
        let { childBlocks } = this;

        const containerChildren = childBlocks.filter(
            block => block instanceof ContainerableBlock
        );

        containerChildren.forEach((block: TContainerableBlock) => {
            childBlocks = childBlocks.concat(block.allDescendants());
        });

        return childBlocks;
    }

    @action
    addChild(child: TBlock, order?: number, childSize?: number) {
        const computedOrder =
            order ??
            get(
                maxBy(this.children, _child => _child.order),
                'order',
                1
            ) + 1;

        this.children.push({
            block_id: child.block_id,
            size: childSize,
            order: computedOrder
        });

        if (!childSize) {
            this.updateChildrenSizes();
        }
    }

    @action
    removeChild(child: TBlock) {
        this.children = reject(this.children, childBlock => {
            return childBlock.block_id === child.block_id;
        });

        this.updateChildrenSizes();
    }

    @action
    updateChildrenSizes() {
        this.children.forEach((child: IChildLayout) => {
            child.size = 100 / (this.children.length || 1);
        });
    }

    @action
    updateChild(index: number, attrs: IChildLayout) {
        this.children[index] = merge(this.children[index], attrs);
    }

    isBackgroundImagePlaceholer() {
        return false;
    }

    getDataForServer(): ContainerableBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as TContainerableBlockType,
            source_flow_id: this.source_flow_id,
            source_block_id: this.source_block_id,
            layout_type: this.layout_type,
            children: this.children
        };
    }

    @computed
    get sourceBlockTemplate() {
        if (!this.source_flow_id) {
            return null;
        }

        return this.rootStore.companyStore?.currCompany.getBlockTemplate(
            this.source_flow_id
        );
    }

    @action
    mergeChildrenAttributes(children: IChildLayoutResponse[]) {
        if (!children || !children.length) {
            return;
        }

        const blockIdToChildren = children.reduce((result, child) => {
            result[child.block_id] = child;

            return result;
        }, {});

        this.children.forEach(localChild => {
            const childFromServer = blockIdToChildren[localChild.block_id];

            Object.assign(localChild, childFromServer);
        });
    }

    get isChangeColumnsDisabled() {
        return false;
    }
}
